import moment from 'moment'
import {
  CardMeta,
  CardMetaGrid,
  ManagementCard,
  ManagementCardList,
  Pill,
} from 'components/ManagementCard'
import { DATE_FORMAT } from '../../constants'
import { commonStrings, oaCreationStrings } from 'constants/strings'
import {
  EOaCreationJobStatus,
  type TOaCreationJobListItem,
} from 'types'

const statusTone: Record<
  EOaCreationJobStatus,
  'success' | 'danger' | 'accent' | 'warning' | 'neutral'
> = {
  [EOaCreationJobStatus.PENDING]: 'neutral',
  [EOaCreationJobStatus.RUNNING]: 'accent',
  [EOaCreationJobStatus.PAUSED]: 'warning',
  [EOaCreationJobStatus.COMPLETED]: 'success',
  [EOaCreationJobStatus.FAILED]: 'danger',
  [EOaCreationJobStatus.ROLLED_BACK]: 'warning',
  [EOaCreationJobStatus.ROLLBACK_TODO]: 'warning',
  [EOaCreationJobStatus.CANCELLED]: 'neutral',
}

const formatProgress = (job: TOaCreationJobListItem): string => {
  if (job.failedCount > 0) {
    return oaCreationStrings.table.progressWithFailedTemplate
      .replace('$success', String(job.successCount))
      .replace('$failed', String(job.failedCount))
      .replace('$total', String(job.quantity))
  }
  return oaCreationStrings.table.progressTemplate
    .replace('$success', String(job.successCount))
    .replace('$total', String(job.quantity))
}

type JobsHistoryCardListProps = {
  jobs: TOaCreationJobListItem[]
  loading: boolean
  className?: string
}

export function JobsHistoryCardList({
  jobs,
  loading,
  className,
}: JobsHistoryCardListProps) {
  return (
    <ManagementCardList
      loading={loading}
      isEmpty={jobs.length === 0}
      loadingMessage={commonStrings.loading}
      emptyMessage={oaCreationStrings.table.states.empty}
      className={className}
    >
      {jobs.map((job) => (
        <li key={job.id}>
          <ManagementCard
            leading={
              job.iconImageUrl ? (
                <img
                  src={job.iconImageUrl}
                  alt={job.accountName}
                  className="h-10 w-10 shrink-0 rounded-full border border-border/80 object-cover"
                />
              ) : (
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-neutral-25 text-xs text-muted">
                  {job.accountName.charAt(0)}
                </span>
              )
            }
            title={job.accountName}
            badges={
              <Pill tone={statusTone[job.status]} className="ml-auto">
                {oaCreationStrings.status[job.status]}
              </Pill>
            }
          >
            <CardMetaGrid>
              <CardMeta
                label={oaCreationStrings.table.headers.createdAt}
                value={moment(job.createdAt).format(`${DATE_FORMAT} HH:mm`)}
              />
              <CardMeta
                label={oaCreationStrings.table.headers.progress}
                value={formatProgress(job)}
              />
            </CardMetaGrid>
            <CardMeta
              label={oaCreationStrings.table.headers.email}
              value={job.email}
            />
            {job.pauseReason && (
              <p className="text-[11px] text-amber-800">
                <span className="font-semibold">
                  {oaCreationStrings.table.pauseReasonLabel}:
                </span>{' '}
                {job.pauseReason}
              </p>
            )}
          </ManagementCard>
        </li>
      ))}
    </ManagementCardList>
  )
}
